/**
 * lib/calibrationCache.js — Iter 34.1 offline-resilient calibration store.
 *
 * Two layers protect the field user from "GPS works, offline works, but
 * GPS+offline doesn't":
 *
 *   1. Service Worker (public/service-worker.js) caches the calibration
 *      HTTP response. Survives most browser cache resets but is purged
 *      when storage pressure is high or when the SW is unregistered.
 *
 *   2. THIS module — a tiny IndexedDB store keyed by sheet_id, written
 *      every time `useGpsCalibration` successfully fetches calibration.
 *      Survives SW unregistration, hard refreshes, and incidental cache
 *      clears. Read as the FALLBACK when the network request fails AND
 *      the SW cache is unavailable.
 *
 * IDB is intentionally minimal — one objectStore, two methods, no
 * versioning ceremony. Calibration is small (≤8 points + metadata),
 * read once per sheet open, written rarely.
 *
 * NOT used for write-buffering — calibration edits still require a live
 * connection (the role-gated write path is too sensitive to mirror
 * locally and reconcile on a flaky link). Only reads are protected.
 */

const DB_NAME = "fop_calibration_cache_v1";
const STORE = "calibration";

function _openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("no IDB"));
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "sheet_id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Persist the calibration doc for a sheet. Silent on failure — IDB
 *  unavailability must never block the foreground UX. */
export async function writeCalibrationCache(sheetId, doc) {
  if (!sheetId || !doc) return;
  try {
    const db = await _openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const payload = { ...doc, sheet_id: sheetId, _cached_at: new Date().toISOString() };
      tx.objectStore(STORE).put(payload);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {/* swallow */}
}

/** Read the last-known calibration for a sheet, or null. */
export async function readCalibrationCache(sheetId) {
  if (!sheetId) return null;
  try {
    const db = await _openDb();
    const out = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(sheetId);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return out;
  } catch {
    return null;
  }
}
